import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { port } from '../../App';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { HrmStore } from '../../Context/HrmContext';


const LocalLoader = () => (
    <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
    </div>
);

const INTERVIEW_STAGES = [
    { code: 'follow_up', display_name: 'Follow Up' },
    { code: 'consider_to_client', display_name: 'Consider to Client' },
    { code: 'internal_hired', display_name: 'Internal Hired' },
    { code: 'closed', display_name: 'Closed' }
];

const CLIENT_STAGES = [
    { code: 'follow_up', display_name: 'Follow Up' },
    { code: 'consider_to_client', display_name: 'Consider to Client' },
    { code: 'prospect', display_name: 'Prospect' },
    { code: 'closed', display_name: 'Closed' }
];

const UniversalLeadsDashboard = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { convertToReadableDateTime } = useContext(HrmStore);

    // Session user details
    const user = JSON.parse(sessionStorage.getItem('user'));
    const loginEmpId = user?.EmployeeId;
    const isManagerOrAdmin = user?.Disgnation === 'Admin' || user?.Disgnation === 'HR' || user?.Designation === 'Admin' || user?.Designation === 'HR';
    const canAssignLeads = isManagerOrAdmin || user?.user_permissions?.lead_assign_access;

    // State management
    const [leads, setLeads] = useState([]);
    const [recruiters, setRecruiters] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'all_leads');

    // Pagination state
    const [page, setPage] = useState(() => parseInt(searchParams.get('page')) || 1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Filter and search state
    const [search, setSearch] = useState(() => searchParams.get('search') || '');
    const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
    const [sortBy, setSortBy] = useState(() => searchParams.get('sort_by') || '-Created_Date');
    const [filterType, setFilterType] = useState(() => searchParams.get('filter_type') || 'today');
    const [customStartDate, setCustomStartDate] = useState(() => searchParams.get('start_date') || '');
    const [customEndDate, setCustomEndDate] = useState(() => searchParams.get('end_date') || '');
    const [startTime, setStartTime] = useState(() => searchParams.get('start_time') || '');
    const [endTime, setEndTime] = useState(() => searchParams.get('end_time') || '');

    // Timeline Selection Modal states
    const [showTimelineModal, setShowTimelineModal] = useState(false);
    const [localFilterType, setLocalFilterType] = useState(filterType);
    const [localStartDate, setLocalStartDate] = useState(customStartDate);
    const [localEndDate, setLocalEndDate] = useState(customEndDate);
    const [localStartTime, setLocalStartTime] = useState(startTime);
    const [localEndTime, setLocalEndTime] = useState(endTime);

    // Advanced Global filters state
    const [globalLocation, setGlobalLocation] = useState(() => searchParams.get('location') || '');
    const [locationInput, setLocationInput] = useState(() => searchParams.get('location') || '');

    const [globalSalary, setGlobalSalary] = useState(() => searchParams.get('salary') || '');
    const [salaryInput, setSalaryInput] = useState(() => searchParams.get('salary') || '');

    const [globalRole, setGlobalRole] = useState(() => searchParams.get('role') || '');
    const [roleInput, setRoleInput] = useState(() => searchParams.get('role') || '');

    const [globalExp, setGlobalExp] = useState(() => searchParams.get('exp') || '');
    const [expInput, setExpInput] = useState(() => searchParams.get('exp') || '');

    const [globalFresh, setGlobalFresh] = useState(() => searchParams.get('fresh') || '');
    const [globalAttended, setGlobalAttended] = useState(() => searchParams.get('attended') || '');

    // Multi-selection state
    const [selectedLeads, setSelectedLeads] = useState([]);
    const [selectedLeadsData, setSelectedLeadsData] = useState({});
    const [assignTargetRecruiter, setAssignTargetRecruiter] = useState('');

    // Dynamic stages state
    const [stages, setStages] = useState([]);



    // Action Modal state (Edit Candidate Lead)
    const [showActionModal, setShowActionModal] = useState(false);
    const [selectedLeadForModal, setSelectedLeadForModal] = useState(null);
    const [modalCandidateName, setModalCandidateName] = useState('');
    const [modalCandidateEmail, setModalCandidateEmail] = useState('');
    const [modalCandidatePhone, setModalCandidatePhone] = useState('');
    const [modalCandidateDesignation, setModalCandidateDesignation] = useState('');
    const [modalSource, setModalSource] = useState('');
    const [modalRemarks, setModalRemarks] = useState('');
    const [modalRecruiter, setModalRecruiter] = useState('');
    const [isSavingModal, setIsSavingModal] = useState(false);

    // (Status change and follow-up scheduling features have been removed from this dashboard)
    const [activeDropdownInterviewId, setActiveDropdownInterviewId] = useState(null);

    const handleInterviewAttendanceSelect = async (lead, newStatus) => {
        try {
            await axios.patch(`${port}/root/create-daily-achieved-activity/${lead.id}`, {
                interview_attendance: newStatus
            });
            toast.success(`Interview status updated to ${newStatus}`);
            fetchUniversalLeads();
        } catch (err) {
            console.error("Error updating interview status:", err);
            toast.error("Failed to update interview status");
        }
    };

    // Column wise filters — keys used for both state and URL (prefixed with cf_ in URL)
    const colFilterKeys = ['candidate_name', 'id', 'candidate_email', 'candidate_phone', 'Created_Date', 'source', 'candidate_designation', 'lead_stage', 'current_status', 'interview_attendance', 'interview_status', 'interview_scheduled_date', 'handled_by', 'assigned_by', 'interview_call_remarks', 'next_followup'];

    // Initialise from URL search params (cf_ prefix)
    const buildColFiltersFromURL = () => {
        const filters = {};
        colFilterKeys.forEach(key => {
            filters[key] = searchParams.get(`cf_${key}`) || '';
        });
        return filters;
    };

    const [colFilters, setColFilters] = useState(buildColFiltersFromURL);
    const colFiltersInitRef = useRef(true);

    // Sync colFilters → URL (debounced)
    useEffect(() => {
        if (colFiltersInitRef.current) {
            colFiltersInitRef.current = false;
            return;
        }
        const timer = setTimeout(() => {
            const params = new URLSearchParams(searchParams);
            // Remove old cf_ params first
            [...params.keys()].forEach(k => { if (k.startsWith('cf_')) params.delete(k); });
            // Add active column filters
            Object.entries(colFilters).forEach(([key, val]) => {
                if (val) params.set(`cf_${key}`, val);
            });
            // Reset to page 1 on column filter change
            params.set('page', '1');
            setSearchParams(params, { replace: true });
        }, 400);
        return () => clearTimeout(timer);
    }, [colFilters]);

    const clearColFilters = () => {
        const empty = {};
        colFilterKeys.forEach(key => { empty[key] = ''; });
        setColFilters(empty);
    };

    const clearAllFilters = () => {
        clearColFilters();
        setLocationInput('');
        setGlobalLocation('');
        setSalaryInput('');
        setGlobalSalary('');
        setRoleInput('');
        setGlobalRole('');
        setExpInput('');
        setGlobalExp('');
        setGlobalFresh('');
        setGlobalAttended('');
        setSearchInput('');
        setSearch('');
        setPage(1);
    };

    const isAnyColFilterActive = Object.values(colFilters).some(val => val !== '');
    const isAnyFilterActive = isAnyColFilterActive ||
        globalLocation || globalSalary || globalRole || globalExp || globalFresh || globalAttended || search;

    const getFilterTypeDisplayName = () => {
        const mapping = {
            'today': 'Today',
            'yesterday': 'Yesterday',
            'this_week': 'This Week',
            'last_week': 'Last Week',
            'this_month': 'This Month',
            'prev_month': 'Last Month',
            'all': 'Timeline',
            'all_time': 'Timeline',
            'timeline': 'Timeline',
            'custom': 'Custom Range'
        };
        return mapping[filterType] || 'Timeline';
    };

    const getCalculatedDateString = (type, bound) => {
        const today = new Date();
        const formatDate = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };

        if (type === 'today') {
            return formatDate(today);
        } else if (type === 'yesterday') {
            const yesterday = new Date();
            yesterday.setDate(today.getDate() - 1);
            return formatDate(yesterday);
        } else if (type === 'this_week') {
            const dayOfWeek = today.getDay();
            const startOfWeek = new Date(today);
            const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
            startOfWeek.setDate(diff);
            if (bound === 'start') {
                return formatDate(startOfWeek);
            } else {
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                return formatDate(endOfWeek);
            }
        } else if (type === 'last_week') {
            const dayOfWeek = today.getDay();
            const startOfLastWeek = new Date(today);
            const diff = today.getDate() - dayOfWeek - 6;
            startOfLastWeek.setDate(diff);
            if (bound === 'start') {
                return formatDate(startOfLastWeek);
            } else {
                const endOfLastWeek = new Date(startOfLastWeek);
                endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
                return formatDate(endOfLastWeek);
            }
        } else if (type === 'this_month') {
            if (bound === 'start') {
                return formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
            } else {
                return formatDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
            }
        } else if (type === 'prev_month' || type === 'last_month') {
            if (bound === 'start') {
                return formatDate(new Date(today.getFullYear(), today.getMonth() - 1, 1));
            } else {
                return formatDate(new Date(today.getFullYear(), today.getMonth(), 0));
            }
        } else if (type === 'all' || type === 'all_time' || type === 'timeline') {
            if (bound === 'start') {
                return '2000-01-01';
            } else {
                return '2100-01-01';
            }
        }
        return '';
    };

    const handleCustomDateChange = (isStart, val) => {
        setLocalFilterType('custom');
        if (isStart) {
            setLocalStartDate(val);
        } else {
            setLocalEndDate(val);
        }
    };

    useEffect(() => {
        if (showTimelineModal) {
            setLocalFilterType(filterType);
            setLocalStartDate(customStartDate || getCalculatedDateString(filterType, 'start'));
            setLocalEndDate(customEndDate || getCalculatedDateString(filterType, 'end'));
            setLocalStartTime(startTime);
            setLocalEndTime(endTime);
        }
    }, [showTimelineModal, filterType, customStartDate, customEndDate, startTime, endTime]);

    // Helper to format date and time to YYYY-MM-DD hh:mm AM/PM
    const formatDateTime = (dateStr) => {
        if (!dateStr) return '—';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        return `${yyyy}-${mm}-${dd} ${hours}:${minutes} ${ampm}`;
    };

    // Helper to format dynamic stage/status names nicely in the UI
    const formatStageName = (code, displayName) => {
        const overrides = {
            'walkin': 'Interview Attended',
            'joined': 'Joined',
            'offer': 'Offered',
            'rejected': 'Rejected',
            'to_client': 'Consider to Client',
            'interview_scheduled': 'Interview Scheduled',
            'call_notpicked': 'Call Not Picked',
            'dis_connect': 'Disconnect',
            'will_revert_back': 'Will Revert Back',
            'newlead': 'Newlead'
        };

        if (code && overrides[code.toLowerCase()]) {
            return overrides[code.toLowerCase()];
        }

        const nameToFormat = displayName || code || '';
        return nameToFormat
            .replace(/[_-]/g, ' ')
            .split(' ')
            .filter(Boolean)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Fetch unified leads from backend
    const fetchUniversalLeads = () => {
        setIsLoading(true);
        const params = new URLSearchParams({
            login_emp_id: loginEmpId,
            page: page,
            page_size: pageSize,
            search: search,
            sort_by: sortBy,
            filter_type: filterType,
            tab: activeTab
        });

        if (filterType === 'custom') {
            if (customStartDate) params.append('start_date', customStartDate);
            if (customEndDate) params.append('end_date', customEndDate);
        }
        if (startTime) params.append('start_time', startTime);
        if (endTime) params.append('end_time', endTime);

        // Add global filters to API parameters
        if (globalLocation) params.append('location', globalLocation);
        if (globalSalary) params.append('salary', globalSalary);
        if (globalRole) params.append('role', globalRole);
        if (globalExp) params.append('exp', globalExp);
        if (globalFresh) params.append('fresh', globalFresh);
        if (globalAttended) params.append('attended', globalAttended);

        // Add column level filters to API parameters
        Object.entries(colFilters).forEach(([key, val]) => {
            if (val && key !== 'interview_scheduled_date' && key !== 'next_followup') {
                params.append(key, val);
            }
        });

        axios.get(`${port}/root/activity/universal-leads/?${params.toString()}`)
            .then(res => {
                setLeads(res.data.results || []);
                setTotalCount(res.data.total_count || 0);
                setTotalPages(res.data.total_pages || 1);
            })
            .catch(err => {
                console.error("Error fetching universal leads:", err);
                toast.error("Failed to load universal leads list.");
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    // Fetch active recruiters for assignment dropdown
    const fetchRecruiters = () => {
        axios.get(`${port}/root/ScreeningAssigning/`)
            .then(res => {
                setRecruiters(res.data || []);
            })
            .catch(err => {
                console.error("Error fetching recruiters:", err);
            });
    };

    // Fetch dynamic stages
    const fetchStages = () => {
        axios.get(`${port}/root/lead-stages/`)
            .then(res => {
                setStages(res.data || []);
            })
            .catch(err => {
                console.error("Error fetching stages:", err);
            });
    };

    // Trigger leads fetch on state changes
    useEffect(() => {
        if (loginEmpId) {
            fetchUniversalLeads();
        }
    }, [page, pageSize, search, sortBy, filterType, customStartDate, customEndDate, activeTab, globalLocation, globalSalary, globalRole, globalExp, globalFresh, globalAttended, startTime, endTime, searchParams]);

    // Sync states to URL search parameters
    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        params.set('tab', activeTab);
        if (page !== 1) params.set('page', page); else params.delete('page');
        if (search) params.set('search', search); else params.delete('search');
        if (sortBy !== '-Created_Date') params.set('sort_by', sortBy); else params.delete('sort_by');
        if (filterType !== 'today') params.set('filter_type', filterType); else params.delete('filter_type');
        if (filterType === 'custom' && customStartDate) params.set('start_date', customStartDate); else params.delete('start_date');
        if (filterType === 'custom' && customEndDate) params.set('end_date', customEndDate); else params.delete('end_date');

        if (globalLocation) params.set('location', globalLocation); else params.delete('location');
        if (globalSalary) params.set('salary', globalSalary); else params.delete('salary');
        if (globalRole) params.set('role', globalRole); else params.delete('role');
        if (globalExp) params.set('exp', globalExp); else params.delete('exp');
        if (globalFresh) params.set('fresh', globalFresh); else params.delete('fresh');
        if (globalAttended) params.set('attended', globalAttended); else params.delete('attended');

        if (startTime) params.set('start_time', startTime); else params.delete('start_time');
        if (endTime) params.set('end_time', endTime); else params.delete('end_time');

        setSearchParams(params, { replace: true });
    }, [page, search, sortBy, filterType, customStartDate, customEndDate, activeTab, globalLocation, globalSalary, globalRole, globalExp, globalFresh, globalAttended, startTime, endTime]);

    // Debounce global search input
    useEffect(() => {
        const handler = setTimeout(() => {
            setSearch(searchInput);
            setPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchInput]);

    // Sync local input states when search change externally
    useEffect(() => {
        setSearchInput(search);
    }, [search]);

    // Debounce global filters
    useEffect(() => {
        const handler = setTimeout(() => {
            setGlobalLocation(locationInput);
            setPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [locationInput]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setGlobalSalary(salaryInput);
            setPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [salaryInput]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setGlobalRole(roleInput);
            setPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [roleInput]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setGlobalExp(expInput);
            setPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [expInput]);

    // Sync input states when global states change externally (e.g., reset)
    useEffect(() => {
        setLocationInput(globalLocation);
    }, [globalLocation]);

    useEffect(() => {
        setSalaryInput(globalSalary);
    }, [globalSalary]);

    useEffect(() => {
        setRoleInput(globalRole);
    }, [globalRole]);

    useEffect(() => {
        setExpInput(globalExp);
    }, [globalExp]);

    useEffect(() => {
        fetchRecruiters();
        fetchStages();
    }, []);

    // Selection handlers
    const handleSelectAll = (e) => {
        const currentPageIds = leads.map(lead => lead.id);
        if (e.target.checked) {
            setSelectedLeads(prev => {
                const newSelection = [...prev];
                currentPageIds.forEach(id => {
                    if (!newSelection.includes(id)) {
                        newSelection.push(id);
                    }
                });
                return newSelection;
            });
            setSelectedLeadsData(prev => {
                const newDetails = { ...prev };
                leads.forEach(lead => {
                    newDetails[lead.id] = lead;
                });
                return newDetails;
            });
        } else {
            setSelectedLeads(prev => prev.filter(id => !currentPageIds.includes(id)));
            setSelectedLeadsData(prev => {
                const next = { ...prev };
                currentPageIds.forEach(id => {
                    delete next[id];
                });
                return next;
            });
        }
    };

    const handleSelectLead = (e, id) => {
        const leadObj = leads.find(l => l.id === id);
        if (e.target.checked) {
            setSelectedLeads(prev => [...prev, id]);
            if (leadObj) {
                setSelectedLeadsData(prev => ({ ...prev, [id]: leadObj }));
            }
        } else {
            setSelectedLeads(prev => prev.filter(item => item !== id));
            setSelectedLeadsData(prev => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
        }
    };

    const handleDownloadCSV = () => {
        const headers = ["Name", "Mobile Number", "Position Applied"];
        const rows = selectedLeads.map(id => {
            const lead = selectedLeadsData[id] || {};
            const name = lead.candidate_name || "N/A";
            const phone = lead.candidate_phone || "N/A";
            const designation = lead.candidate_designation || "N/A";
            return [
                `"${name.replace(/"/g, '""')}"`,
                `"${phone}"`,
                `"${designation.replace(/"/g, '""')}"`
            ];
        });

        const csvContent = "data:text/csv;charset=utf-8," 
            + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
        
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Selected_Leads_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Bulk assign leads to a recruiter (supports only sourced daily leads or candidates)
    const handleBulkAssign = () => {
        if (!selectedLeads.length) {
            toast.warning("Please select at least one lead.");
            return;
        }
        if (!assignTargetRecruiter) {
            toast.warning("Please select a recruiter to assign to.");
            return;
        }

        // Separate ids
        const candidateLeadIds = [];
        const sourcedLeadIds = [];
        selectedLeads.forEach(idStr => {
            if (idStr.startsWith('c_')) {
                candidateLeadIds.push(parseInt(idStr.replace('c_', '')));
            } else {
                sourcedLeadIds.push(parseInt(idStr));
            }
        });

        axios.post(`${port}/root/activity/universal-leads/`, {
            lead_ids: sourcedLeadIds,
            candidate_ids: candidateLeadIds,
            assign_to_emp_ids: [assignTargetRecruiter],
            login_emp_id: loginEmpId
        })
            .then(res => {
                toast.success(res.data.message || "Successfully assigned leads.");
                setSelectedLeads([]);
                setSelectedLeadsData({});
                setAssignTargetRecruiter('');
                fetchUniversalLeads();
            })
            .catch(err => {
                console.error("Assignment error:", err);
                toast.error(err.response?.data?.error || "Failed to assign leads.");
            });
    };

    // Open Action Modal with lead pre-filled details (For sourced daily leads only)
    const handleOpenActionModal = (lead) => {
        if (lead.lead_type !== 'sourced') return;
        setSelectedLeadForModal(lead);
        setModalCandidateName(lead.candidate_name || '');
        setModalCandidateEmail(lead.candidate_email || '');
        setModalCandidatePhone(lead.candidate_phone || '');
        setModalCandidateDesignation(lead.candidate_designation || '');
        setModalSource(lead.source || '');
        setModalRemarks(lead.interview_call_remarks || '');
        setModalRecruiter(lead.handled_by_id?.[0] || '');
        setShowActionModal(true);
    };

    // Save Action Modal changes (For sourced daily leads only)
    const handleSaveModalChanges = async () => {
        if (!selectedLeadForModal) return;

        if (!modalRemarks || !modalRemarks.trim()) {
            toast.warning("Remarks/comments are mandatory.");
            return;
        }

        setIsSavingModal(true);
        try {
            const patchData = {};
            if (modalCandidateName !== (selectedLeadForModal.candidate_name || '')) {
                patchData.candidate_name = modalCandidateName;
            }
            if (modalCandidateEmail !== (selectedLeadForModal.candidate_email || '')) {
                patchData.candidate_email = modalCandidateEmail;
            }
            if (modalCandidatePhone !== (selectedLeadForModal.candidate_phone || '')) {
                patchData.candidate_phone = modalCandidatePhone;
            }
            if (modalCandidateDesignation !== (selectedLeadForModal.candidate_designation || '')) {
                patchData.candidate_designation = modalCandidateDesignation;
            }
            if (modalSource !== (selectedLeadForModal.source || '')) {
                patchData.source = modalSource;
            }
            if (modalRemarks !== (selectedLeadForModal.interview_call_remarks || '')) {
                patchData.interview_call_remarks = modalRemarks;
            }

            // Save Details & Remarks if changed
            if (Object.keys(patchData).length > 0) {
                await axios.patch(`${port}/root/create-daily-achieved-activity/${selectedLeadForModal.original_id}`, patchData);
            }

            // Save Recruiter reassignment if changed (HR / Admins/Permitted only)
            if (canAssignLeads && modalRecruiter && modalRecruiter !== (selectedLeadForModal.handled_by_id?.[0] || '')) {
                await axios.post(`${port}/root/activity/leads/`, {
                    lead_ids: [selectedLeadForModal.original_id],
                    assign_to_emp_id: modalRecruiter,
                    login_emp_id: loginEmpId
                });
            }

            toast.success("Lead updated successfully.");
            setShowActionModal(false);
            fetchUniversalLeads();
        } catch (err) {
            console.error("Save lead changes error:", err);
            toast.error("Failed to save changes.");
        } finally {
            setIsSavingModal(false);
        }
    };

    // (Stage/Status Change and Follow-up creation handlers have been removed to restrict edits)

    // Helper functions for badge colors
    const getLeadBadgeLabel = (lead) => {
        if (lead.lead_stage) {
            const stageName = formatStageName(lead.lead_stage);
            if (lead.current_status) {
                return `${stageName} (${formatStageName(lead.current_status)})`;
            }
            return stageName;
        }
        const legacyStatus = lead.interview_status || lead.client_status;
        return legacyStatus ? formatStageName(legacyStatus) : (lead.lead_type === 'sourced' ? 'Newlead' : 'Pending');
    };

    const getLeadBadgeClass = (lead) => {
        const stage = lead.lead_stage || lead.interview_status || lead.client_status || '';
        const subStatus = lead.current_status || '';
        const checkValue = (subStatus || stage).toLowerCase();

        switch (checkValue) {
            case 'joined':
            case 'candidate_joined':
                return 'bg-transparent text-green-600 border-green-500';
            case 'offer':
            case 'offered':
                return 'bg-transparent text-blue-600 border-blue-500';
            case 'interview_scheduled':
            case 'interview-scheduled':
            case 'assigned':
                return 'bg-transparent text-purple-600 border-purple-500';
            case 'walkin':
            case 'interview_attended':
                return 'bg-transparent text-amber-600 border-amber-500';
            case 'reject':
            case 'rejected':
            case 'rejected_by_candidate':
                return 'bg-transparent text-red-600 border-red-500';
            case 'call_notpicked':
            case 'dis_connect':
            case 'disconnect':
            case 'will_revert_back':
            case 'follow_up':
                return 'bg-transparent text-slate-600 border-slate-400';
            case 'pending':
                return 'bg-transparent text-amber-500 border-amber-400';
            default:
                return 'bg-transparent text-indigo-600 border-indigo-500';
        }
    };

    const getStatusBadgeClass = (status) => {
        return getLeadBadgeClass({ interview_status: status });
    };

    const getStageLabel = (lead) => {
        const stage = lead.lead_stage || lead.interview_status || lead.client_status;
        return stage ? formatStageName(stage) : (lead.lead_type === 'sourced' ? 'Newlead' : 'Pending');
    };

    const getStageClass = (lead) => {
        const stage = lead.lead_stage || lead.interview_status || lead.client_status || '';
        const checkValue = stage.toLowerCase();
        switch (checkValue) {
            case 'interview_scheduled':
            case 'assigned':
                return 'bg-transparent text-purple-600 border-purple-500';
            case 'walkin':
            case 'interview_attended':
                return 'bg-transparent text-amber-600 border-amber-500';
            case 'closed':
                return 'bg-transparent text-rose-600 border-rose-500';
            case 'follow_up':
                return 'bg-transparent text-slate-600 border-slate-400';
            case 'consider_to_client':
            case 'internal_hired':
                return 'bg-transparent text-indigo-600 border-indigo-500';
            case 'pending':
                return 'bg-transparent text-amber-500 border-amber-400';
            default:
                return 'bg-transparent text-indigo-600 border-indigo-500';
        }
    };

    const getStatusLabel = (lead) => {
        if (lead.lead_stage) {
            return lead.current_status ? formatStageName(lead.current_status) : '—';
        }
        const status = lead.interview_status || lead.client_status;
        if (!status) return '—';
        const isMainStage = ['interview_scheduled', 'walkin', 'interview_attended', 'closed', 'newlead', 'pending', 'assigned'].includes(status.toLowerCase());
        if (isMainStage) return '—';
        return formatStageName(status);
    };

    const getStatusClass = (lead) => {
        const statusLabel = getStatusLabel(lead);
        if (statusLabel === '—') return 'text-slate-400 font-medium';
        const checkValue = (lead.current_status || lead.interview_status || lead.client_status || '').toLowerCase();
        switch (checkValue) {
            case 'joined':
            case 'candidate_joined':
                return 'bg-transparent text-green-600 border-green-500';
            case 'offer':
            case 'offered':
                return 'bg-transparent text-blue-600 border-blue-500';
            case 'rejected':
            case 'reject':
            case 'rejected_by_candidate':
                return 'bg-transparent text-red-600 border-red-500';
            case 'call_notpicked':
            case 'dis_connect':
            case 'disconnect':
            case 'will_revert_back':
                return 'bg-transparent text-slate-600 border-slate-400';
            default:
                return 'bg-transparent text-slate-600 border-slate-400';
        }
    };

    // Front-end local filtering logic for columns like dates or scheduling
    const filteredLeads = leads.filter(lead => {
        return Object.entries(colFilters).every(([key, val]) => {
            if (!val) return true;

            if (key === 'id') {
                return String(lead.original_id).toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'candidate_name') {
                return (lead.candidate_name || '').toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'candidate_email') {
                return (lead.candidate_email || '').toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'candidate_phone') {
                return (lead.candidate_phone || '').toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'source') {
                return (lead.source || '').toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'candidate_designation') {
                return (lead.candidate_designation || '').toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'lead_stage') {
                const stage = lead.lead_stage || lead.interview_status || lead.client_status || '';
                return stage.toLowerCase() === val.toLowerCase();
            }
            if (key === 'current_status') {
                const status = lead.current_status || lead.interview_status || lead.client_status || '';
                return status.toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'interview_status') {
                return (lead.interview_status || '').toLowerCase() === val.toLowerCase();
            }
            if (key === 'interview_attendance') {
                return (lead.interview_attendance || '').toLowerCase() === val.toLowerCase();
            }
            if (key === 'interview_scheduled_date') {
                const isd = lead.interview_scheduled_date ? formatDateTime(lead.interview_scheduled_date).toLowerCase() : '';
                return isd.includes(val.toLowerCase());
            }
            if (key === 'handled_by') {
                return (lead.handled_by || '').toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'assigned_by') {
                return (lead.assigned_by || '').toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'interview_call_remarks') {
                return (lead.interview_call_remarks || '').toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'Created_Date') {
                const dateStr = formatDateTime(lead.Created_Date).toLowerCase();
                return dateStr.includes(val.toLowerCase());
            }
            if (key === 'next_followup') {
                const fdate = lead.next_followup_date ? new Date(lead.next_followup_date).toLocaleDateString('en-GB').replace(/\//g, '-') : '';
                return fdate.toLowerCase().includes(val.toLowerCase());
            }

            return true;
        });
    });

    return (
        <div className="container-fluid p-4 font-popins text-slate-800 poppins">
            {/* Top Header Card */}
            <div className="bg-blue-600 text-white rounded-xl px-4 py-3 flex justify-between items-center shadow-sm">
                <h5 className="font-bold mb-0 text-sm md:text-base flex items-center gap-3">
                    <button
                        onClick={() => navigate('/activity')}
                        className="bg-transparent border-0 text-white p-0 hover:text-slate-200 transition-colors cursor-pointer flex items-center justify-center"
                        title="Back to Dashboard"
                    >
                        <i className="fas fa-arrow-left text-base"></i>
                    </button>
                    <i className="fas fa-globe-americas"></i> Universal Leads Dashboard
                </h5>
            </div>

            {/* Filter controls subbar */}
            <div className="flex justify-between items-center my-3 px-1 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                    {/* Global Search Input */}
                    <div className="flex flex-col relative">
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-[12px] poppins text-slate-500 font-semibold uppercase tracking-wider">Search Leads</span>
                            <OverlayTrigger
                                placement="top"
                                delay={{ show: 150, hide: 250 }}
                                overlay={
                                    <Tooltip id="search-tooltip" className="shadow-lg">
                                        <div className="text-center text-xs p-1">
                                            Search by Candidate name, ID, Email, Mobile number, Source, Designation, Status, Handled by, Assigned by, Remarks.
                                        </div>
                                    </Tooltip>
                                }
                            >
                                <span className="cursor-pointer text-slate-400 hover:text-slate-600 flex items-center justify-center">
                                    <i className="fas fa-info-circle text-[11px]"></i>
                                </span>
                            </OverlayTrigger>
                        </div>
                        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 shadow-sm w-[280px]">
                            <i className="fas fa-search text-slate-400 text-xs"></i>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 p-0 w-full placeholder:text-slate-400"
                            />
                            {searchInput && (
                                <button
                                    onClick={() => { setSearchInput(''); setSearch(''); }}
                                    className="border-0 bg-transparent p-0 text-slate-400 hover:text-slate-600 flex items-center justify-center"
                                >
                                    <i className="fas fa-times-circle text-xs"></i>
                                </button>
                            )}
                        </div>
                    </div>

                    {canAssignLeads && selectedLeads.length > 0 && (
                        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 shadow-sm">
                            <input
                                type="checkbox"
                                checked={true}
                                onChange={() => {
                                    setSelectedLeads([]);
                                    setSelectedLeadsData({});
                                }}
                                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-xs font-semibold text-slate-600">
                                Selected ({selectedLeads.length}) leads
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {selectedLeads.length > 0 && (
                        <button
                            onClick={handleDownloadCSV}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg text-xs shadow-sm transition-all"
                            title="Download CSV"
                        >
                            <i className="fas fa-download text-[11px]"></i> CSV
                        </button>
                    )}
                    {canAssignLeads && (
                        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-sm">
                            <span className="text-sm text-slate-500 font-medium">Assign to:</span>
                            <select
                                value={assignTargetRecruiter}
                                onChange={(e) => setAssignTargetRecruiter(e.target.value)}
                                className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 py-0"
                            >
                                <option value="">Select Employee</option>
                                {recruiters.map((rec) => (
                                    <option key={rec.id} value={rec.EmployeeId}>
                                        {rec.Name}
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={handleBulkAssign}
                                disabled={!selectedLeads.length || !assignTargetRecruiter}
                                className="px-2 py-0.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-100 disabled:text-slate-400 text-white font-semibold rounded text-[10px] transition-all"
                            >
                                Assign
                            </button>
                        </div>
                    )}

                    {/* Timeline Filter Toggle Button */}
                    <button
                        onClick={() => setShowTimelineModal(true)}
                        className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-sm hover:border-slate-300 transition-all duration-150 focus:outline-none"
                        title="Timeline Selection"
                    >
                        <i className="fas fa-sliders-h text-indigo-600 text-xs"></i>
                        <span className="text-sm text-slate-500 font-medium poppins">Timeline:</span>
                        <span className="text-xs font-semibold text-indigo-700">{getFilterTypeDisplayName()}</span>
                    </button>

                    <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-sm">
                        <span className="text-sm text-slate-500 font-medium">Sort By:</span>
                        <select
                            value={sortBy}
                            onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                            className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 py-0"
                        >
                            <option value="-Created_Date">Newest First</option>
                            <option value="Created_Date">Oldest First</option>
                            <option value="candidate_name">Name A-Z</option>
                            <option value="-candidate_name">Name Z-A</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Global Advanced Filters Card */}
            <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm mb-4">
                    <div className="flex justify-between items-center mb-3">
                        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2">
                            <i className="fas fa-sliders-h text-indigo-500"></i> Advanced Filters
                        </span>
                        {isAnyFilterActive && (
                            <button
                                onClick={clearAllFilters}
                                className="text-xs text-red-500 hover:text-red-700 font-semibold flex items-center gap-1.5 transition-colors border-0 bg-transparent focus:outline-none"
                            >
                                <i className="fas fa-trash-alt text-[10px]"></i> Clear Filters
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        {/* Location Filter */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Location</label>
                            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                <i className="fas fa-map-marker-alt text-slate-400 text-[10px]"></i>
                                <input
                                    type="text"
                                    placeholder="Filter by location..."
                                    value={locationInput}
                                    onChange={(e) => setLocationInput(e.target.value)}
                                    className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 p-0 w-full placeholder:text-slate-400"
                                />
                            </div>
                        </div>

                        {/* Salary Filter */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Salary (CTC)</label>
                            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                <i className="fas fa-money-bill-wave text-slate-400 text-[10px]"></i>
                                <input
                                    type="text"
                                    placeholder="Filter by salary..."
                                    value={salaryInput}
                                    onChange={(e) => setSalaryInput(e.target.value)}
                                    className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 p-0 w-full placeholder:text-slate-400"
                                />
                            </div>
                        </div>

                        {/* Role Filter */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Role / Designation</label>
                            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                <i className="fas fa-briefcase text-slate-400 text-[10px]"></i>
                                <input
                                    type="text"
                                    placeholder="Filter by role..."
                                    value={roleInput}
                                    onChange={(e) => setRoleInput(e.target.value)}
                                    className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 p-0 w-full placeholder:text-slate-400"
                                />
                            </div>
                        </div>

                        {/* Exp Filter */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Experience</label>
                            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                <i className="fas fa-history text-slate-400 text-[10px]"></i>
                                <input
                                    type="text"
                                    placeholder="Filter by exp..."
                                    value={expInput}
                                    onChange={(e) => setExpInput(e.target.value)}
                                    className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 p-0 w-full placeholder:text-slate-400"
                                />
                            </div>
                        </div>

                        {/* Fresh Filter */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Type (Fresher/Exp)</label>
                            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                <i className="fas fa-user-graduate text-slate-400 text-[10px]"></i>
                                <select
                                    value={globalFresh}
                                    onChange={(e) => setGlobalFresh(e.target.value)}
                                    className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 p-0 w-full cursor-pointer focus:outline-none"
                                >
                                    <option value="">All Types</option>
                                    <option value="fresher">Fresher</option>
                                    <option value="experience">Experienced</option>
                                </select>
                            </div>
                        </div>

                        {/* Interview Attended Filter */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Interview Status</label>
                            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                <i className="fas fa-calendar-check text-slate-400 text-[10px]"></i>
                                <select
                                    value={globalAttended}
                                    onChange={(e) => setGlobalAttended(e.target.value)}
                                    className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 p-0 w-full cursor-pointer focus:outline-none"
                                >
                                    <option value="">All Statuses</option>
                                    <option value="attended">Interview Attended</option>
                                    <option value="not_attended">Not Attended</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

            {/* Table */}
            <main className="table-responsive tablebg h-[60vh] overflow-x-auto sm:h-[65vh] md:h-[70vh] lg:h-[70vh] xl:h-[70vh]">
                {isLoading ? (
                    <LocalLoader />
                ) : (
                    <table className="w-full table-fixed min-w-[2000px] border-collapse border border-slate-200">
                        <thead>
                            {/* Column Titles Row */}
                            <tr className="bg-slate-100 text-slate-800 font-semibold text-sm whitespace-nowrap">
                                <th style={{ width: '3%' }} className="p-2 text-center border border-slate-200">
                                    <input
                                        type="checkbox"
                                        onChange={handleSelectAll}
                                        checked={leads.length > 0 && leads.every(lead => selectedLeads.includes(lead.id))}
                                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                </th>
                                <th style={{ width: '4%' }} className="p-2 text-center border border-slate-200">SI No</th>
                                <th style={{ width: '10%' }} className="p-2 text-center border border-slate-200">Name</th>
                                <th style={{ width: '7%' }} className="p-2 text-center border border-slate-200">Cand. ID</th>
                                <th style={{ width: '15%' }} className="p-2 text-center border border-slate-200">Email ID</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Mobile Number</th>
                                <th style={{ width: '16%' }} className="p-2 text-center border border-slate-200">Added Date/Time</th>
                                <th style={{ width: '8%' }} className="p-2 text-center border border-slate-200">Source</th>
                                <th style={{ width: '13%' }} className="p-2 text-center border border-slate-200">Position Applied</th>
                                <th style={{ width: '16%' }} className="p-2 text-center border border-slate-200">Lead Stage</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Current Status</th>
                                <th style={{ width: '14%' }} className="p-2 text-center border border-slate-200">Interview Status</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Interview Date</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Handled By</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Assigned By</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Remarks</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Next Follow-up</th>
                                <th style={{ width: '7%' }} className="p-2 text-center border border-slate-200">Action</th>
                            </tr>

                            {/* Column Filters Row */}
                            <tr className="bg-slate-50 text-slate-700 font-normal text-xs whitespace-nowrap">
                                <th className="p-1.5 text-center border border-slate-200">
                                    {isAnyColFilterActive && (
                                        <button
                                            onClick={clearColFilters}
                                            title="Clear column filters"
                                            className="text-red-500 hover:text-red-700 font-bold focus:outline-none flex items-center justify-center mx-auto"
                                        >
                                            <i className="fas fa-filter-circle-xmark text-sm"></i>
                                        </button>
                                    )}
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <span className="text-slate-400 font-medium">—</span>
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Name"
                                        value={colFilters.candidate_name}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, candidate_name: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="ID"
                                        value={colFilters.id}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, id: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Email"
                                        value={colFilters.candidate_email}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, candidate_email: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Contact"
                                        value={colFilters.candidate_phone}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, candidate_phone: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Date"
                                        value={colFilters.Created_Date}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, Created_Date: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Source"
                                        value={colFilters.source}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, source: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Position"
                                        value={colFilters.candidate_designation}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, candidate_designation: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <select
                                        value={colFilters.lead_stage || ''}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, lead_stage: e.target.value }))}
                                        className="w-full px-1 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal cursor-pointer focus:ring-1 focus:ring-indigo-500"
                                    >
                                        <option value="">Stage</option>
                                        <option value="newlead">New Lead</option>
                                        {INTERVIEW_STAGES.map((stage) => (
                                            <option key={stage.code} value={stage.code}>
                                                {stage.display_name}
                                            </option>
                                        ))}
                                    </select>
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Status"
                                        value={colFilters.current_status || ''}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, current_status: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <select
                                        value={colFilters.interview_attendance || ''}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, interview_attendance: e.target.value }))}
                                        className="w-full px-1 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal cursor-pointer focus:ring-1 focus:ring-indigo-500"
                                    >
                                        <option value="">Status</option>
                                        <option value="Scheduled">Scheduled</option>
                                        <option value="Attended">Attended</option>
                                    </select>
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Interview Date"
                                        value={colFilters.interview_scheduled_date || ''}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, interview_scheduled_date: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-500 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Recruiter"
                                        value={colFilters.handled_by}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, handled_by: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Assigner"
                                        value={colFilters.assigned_by}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, assigned_by: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Remarks"
                                        value={colFilters.interview_call_remarks}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, interview_call_remarks: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Date"
                                        value={colFilters.next_followup}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, next_followup: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <span className="text-slate-400 font-medium">—</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLeads.length === 0 ? (
                                <tr>
                                    <td colSpan="16" className="text-center py-8 italic text-slate-400 border border-slate-200 text-xs">
                                        No universal leads found matching the filters.
                                    </td>
                                </tr>
                            ) : (
                                filteredLeads.map((lead, index) => {
                                    const dropdownPlacementClass = (index >= filteredLeads.length - 4 && filteredLeads.length > 4)
                                        ? 'bottom-full mb-1 origin-bottom-right'
                                        : 'top-full mt-1 origin-top-right';

                                    return (
                                        <tr key={lead.id} className="border-b hover:bg-slate-100">
                                            {/* Selection Box */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLeads.includes(lead.id)}
                                                    onChange={(e) => handleSelectLead(e, lead.id)}
                                                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                            </td>

                                            {/* SI No */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {index + 1 + (page - 1) * pageSize}
                                            </td>

                                            {/* Name */}
                                            <td className="p-2 text-center font-bold text-slate-800 border border-slate-200 text-sm">
                                                {lead.lead_type === 'sourced' ? (
                                                    <a onClick={() => navigate(`/activity/lead-log/${lead.original_id}`)}
                                                        className="font-semibold hover:text-blue-700 cursor-pointer transition-all">{lead.candidate_name || 'N/A'}</a>
                                                ) : (
                                                    <span className="font-semibold">{lead.candidate_name || 'N/A'}</span>
                                                )}
                                            </td>

                                            {/* Cand. ID (original ID) */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {lead.original_id}
                                            </td>

                                            {/* Email ID */}
                                            <td className="p-2 text-center break-all border border-slate-200 text-sm">
                                                {lead.candidate_email || '—'}
                                            </td>

                                            {/* Contact Number */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {lead.candidate_phone && lead.candidate_phone !== '—' ? (
                                                    <a
                                                        href={`tel:${lead.candidate_phone}`}
                                                        className="text-indigo-600 hover:text-indigo-800 hover:underline font-semibold"
                                                    >
                                                        {lead.candidate_phone}
                                                    </a>
                                                ) : '—'}
                                            </td>

                                            {/* Added Date/Time */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {formatDateTime(lead.Created_Date)}
                                            </td>

                                            {/* Source */}
                                            <td className="p-2 text-center capitalize border border-slate-200 text-sm">
                                                {lead.source || 'N/A'}
                                            </td>

                                            {/* Position Applied */}
                                            <td className="p-2 text-center font-semibold text-slate-700 border border-slate-200 text-sm">
                                                {lead.candidate_designation || 'N/A'}
                                            </td>

                                            {/* Lead Stage Badge */}
                                            <td className="p-2 text-center border border-slate-200 text-sm relative">
                                                <span className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border shadow-sm inline-block mx-auto whitespace-nowrap ${getStageClass(lead)}`}>
                                                    {getStageLabel(lead)}
                                                </span>
                                            </td>

                                            {/* Current Status sub-status Badge */}
                                            <td className="p-2 text-center border border-slate-200 text-sm whitespace-nowrap">
                                                {getStatusLabel(lead) !== '—' ? (
                                                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border shadow-sm inline-block ${getStatusClass(lead)}`}>
                                                        {getStatusLabel(lead)}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-400 font-medium">—</span>
                                                )}
                                            </td>

                                            {/* Interview Status Badge Dropdown */}
                                            <td className="p-2 text-center border border-slate-200 text-sm relative">
                                                {(lead.lead_stage === 'follow_up' && lead.interview_attendance) ? (
                                                    <div className="relative inline-block text-left interview-status-dropdown-container">
                                                        <button
                                                            onClick={() => setActiveDropdownInterviewId(activeDropdownInterviewId === lead.id ? null : lead.id)}
                                                            className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border shadow-sm flex items-center gap-1 mx-auto transition-all duration-200 hover:scale-105 whitespace-nowrap ${lead.interview_attendance === 'Attended'
                                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                                    : 'bg-amber-50 text-amber-700 border-amber-200'
                                                                }`}
                                                        >
                                                            {lead.interview_attendance}
                                                            <i className="fas fa-chevron-down text-[8px] opacity-70"></i>
                                                        </button>
                                                        {activeDropdownInterviewId === lead.id && (
                                                            <div className={`absolute right-0 w-32 rounded-lg shadow-xl bg-white border border-slate-100 z-50`}>
                                                                <div className="py-1">
                                                                    {['Scheduled', 'Attended'].map((statusOption) => (
                                                                        <button
                                                                            key={statusOption}
                                                                            onClick={() => {
                                                                                handleInterviewAttendanceSelect(lead, statusOption);
                                                                                setActiveDropdownInterviewId(null);
                                                                            }}
                                                                            className={`block w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-100 transition-all font-semibold ${lead.interview_attendance === statusOption ? 'bg-indigo-50 text-indigo-700' : ''}`}
                                                                        >
                                                                            {statusOption}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-400 font-medium">—</span>
                                                )}
                                            </td>

                                            {/* Interview Date */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {lead.interview_scheduled_date ? (
                                                    <span className="font-semibold text-slate-700">
                                                        {formatDateTime(lead.interview_scheduled_date)}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-slate-400">—</span>
                                                )}
                                            </td>

                                            {/* Handled By */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {lead.handled_by || 'Unassigned'}
                                            </td>

                                            {/* Assigned By */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {lead.assigned_by || 'System'}
                                            </td>

                                            {/* Remarks */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {lead.interview_call_remarks && lead.interview_call_remarks !== '—' ? (
                                                    <OverlayTrigger
                                                        trigger="click"
                                                        placement="top"
                                                        rootClose
                                                        overlay={
                                                            <Tooltip id={`tooltip-remarks-${lead.id}`}>
                                                                {lead.interview_call_remarks}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span className="block truncate max-w-[150px] mx-auto text-xs cursor-pointer text-indigo-600 hover:text-indigo-800 font-semibold hover:underline">
                                                            {lead.interview_call_remarks}
                                                        </span>
                                                    </OverlayTrigger>
                                                ) : (
                                                    <span className="text-xs text-slate-400">—</span>
                                                )}
                                            </td>

                                            {/* Next Follow-up */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {lead.next_followup_date ? (
                                                    <div className="text-xs flex flex-col items-center justify-center max-w-[130px] mx-auto">
                                                        <span className="font-semibold text-slate-700 block">
                                                            {new Date(lead.next_followup_date).toLocaleDateString('en-GB').replace(/\//g, '-')}
                                                        </span>
                                                        <span className="text-[10px] text-slate-400 block font-medium">
                                                            {lead.next_followup_time ?
                                                                new Date(`1970-01-01T${lead.next_followup_time}`).toLocaleTimeString('en-US', {
                                                                    hour: 'numeric',
                                                                    minute: '2-digit',
                                                                    hour12: true
                                                                })
                                                                : ''}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-400">—</span>
                                                )}
                                            </td>

                                            {/* Action */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {lead.lead_type === 'sourced' ? (
                                                    <button
                                                        onClick={() => handleOpenActionModal(lead)}
                                                        className="px-2 py-1 rounded bg-blue-500 text-white text-[10px] font-semibold hover:bg-orange-600 transition-all flex items-center justify-center gap-1 mx-auto"
                                                    >
                                                        <i className="fas fa-edit text-[9px]"></i> Edit
                                                    </button>
                                                ) : (
                                                    <span className="text-slate-400">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                )}
            </main>

            {/* Pagination Footer */}
            {!isLoading && totalCount > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-2 bg-white p-2 rounded shadow-sm font-Poppins flex-wrap gap-3">
                    {/* Left: Total */}
                    <div className="text-sm font-semibold text-slate-600">
                        Total: <strong className="text-indigo-600">{totalCount}</strong>
                    </div>

                    {/* Center: Pagination Buttons */}
                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-primary btn-sm rounded px-3"
                            disabled={page === 1}
                            onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                        >
                            Previous
                        </button>

                        <div className="small px-3 bg-primary text-white rounded py-1 font-semibold">
                            {page} of {totalPages}
                        </div>

                        <button
                            className="btn btn-primary btn-sm rounded px-3"
                            disabled={page === totalPages}
                            onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                        >
                            Next
                        </button>
                    </div>

                    {/* Center-Right: Page Jump */}
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-sm">Page</span>
                        <input
                            type="number"
                            className="form-control form-control-sm"
                            style={{ width: "55px" }}
                            min={1}
                            max={totalPages}
                            value={page}
                            onChange={(e) => {
                                const val = parseInt(e.target.value);
                                const maxLimit = totalPages;
                                if (val >= 1 && val <= maxLimit) {
                                    setPage(val);
                                }
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Action Modal for Edit Candidate Lead (Sourced Leads only) */}
            {showActionModal && selectedLeadForModal && (
                <Modal show={showActionModal} onHide={() => setShowActionModal(false)} centered>
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="text-lg font-bold text-slate-800">
                            Edit Sourced Lead
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-2">
                        <div className="space-y-4">
                            <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 text-sm">
                                <span className="text-xs font-bold text-indigo-600 block mb-2.5 uppercase tracking-wider">Candidate Details</span>
                                <div className="grid grid-cols-2 gap-2.5">
                                    <div className="col-span-2">
                                        <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Candidate Name</label>
                                        <input
                                            type="text"
                                            value={modalCandidateName}
                                            onChange={(e) => setModalCandidateName(e.target.value)}
                                            className="form-control border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full"
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Email ID</label>
                                        <input
                                            type="email"
                                            value={modalCandidateEmail}
                                            onChange={(e) => setModalCandidateEmail(e.target.value)}
                                            className="form-control border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full"
                                        />
                                    </div>
                                    <div className="col-span-2 md:col-span-1">
                                        <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Mobile Number</label>
                                        <input
                                            type="text"
                                            value={modalCandidatePhone}
                                            onChange={(e) => setModalCandidatePhone(e.target.value)}
                                            className="form-control border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full"
                                        />
                                    </div>
                                    <div className="col-span-2 md:col-span-1">
                                        <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Source</label>
                                        <input
                                            type="text"
                                            value={modalSource}
                                            onChange={(e) => setModalSource(e.target.value)}
                                            className="form-control border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full"
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Position Applied</label>
                                        <input
                                            type="text"
                                            value={modalCandidateDesignation}
                                            onChange={(e) => setModalCandidateDesignation(e.target.value)}
                                            className="form-control border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Recruiter (Visible only to Admin/HR/Permitted) */}
                            {canAssignLeads && (
                                <div>
                                    <label className="form-label text-xs font-semibold text-slate-600 mb-1">Assigned Recruiter</label>
                                    <select
                                        value={modalRecruiter}
                                        onChange={(e) => setModalRecruiter(e.target.value)}
                                        className="form-select border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full"
                                    >
                                        <option value="">-- Select Recruiter --</option>
                                        {recruiters.map((rec) => (
                                            <option key={rec.id} value={rec.EmployeeId}>
                                                {rec.Name} ({rec.EmployeeId})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <button
                            onClick={() => setShowActionModal(false)}
                            className="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-700 rounded-lg bg-slate-100 hover:bg-slate-200 transition-all mr-2"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveModalChanges}
                            disabled={isSavingModal}
                            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 transition-all"
                        >
                            {isSavingModal ? 'Saving...' : 'Save Changes'}
                        </button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* (Unused stage change and follow-up schedule modals have been removed) */}

            {showTimelineModal && (
                <Modal
                    show={showTimelineModal}
                    onHide={() => setShowTimelineModal(false)}
                    centered
                    dialogClassName="max-w-[420px]"
                >
                    <div className="bg-white rounded-2xl overflow-hidden shadow-2xl p-6 border border-slate-100 poppins">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="text-xl font-bold text-slate-800 m-0">Timeline Selection</h3>
                                <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest block mt-1">
                                    Currently: {getFilterTypeDisplayName().toUpperCase()}
                                </span>
                            </div>
                            <button
                                onClick={() => setShowTimelineModal(false)}
                                className="text-slate-400 hover:text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-full w-7 h-7 flex items-center justify-center transition-all border-0 focus:outline-none"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        {/* Presets Grid */}
                        <div className="grid grid-cols-3 gap-2 mb-4">
                            {[
                                { code: 'today', label: 'Today' },
                                { code: 'yesterday', label: 'Yesterday' },
                                { code: 'this_week', label: 'This Week' },
                                { code: 'last_week', label: 'Last Week' },
                                { code: 'this_month', label: 'This Month' },
                                { code: 'prev_month', label: 'Last Month' },
                                { code: 'all', label: 'Timeline' }
                            ].map((preset) => {
                                const isActive = localFilterType === preset.code || (preset.code === 'all' && (localFilterType === 'all_time' || localFilterType === 'timeline'));
                                return (
                                    <button
                                        key={preset.code}
                                        onClick={() => {
                                            setLocalFilterType(preset.code);
                                            setLocalStartDate(getCalculatedDateString(preset.code, 'start'));
                                            setLocalEndDate(getCalculatedDateString(preset.code, 'end'));
                                        }}
                                        className={`py-2 text-[11px] font-bold uppercase tracking-wider rounded-xl transition-all duration-200 border text-center ${preset.code === 'all' ? 'col-span-3' : ''
                                            } ${isActive
                                                ? 'bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100'
                                                : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200'
                                            }`}
                                    >
                                        {preset.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Divider */}
                        <hr className="border-slate-100 my-4" />

                        {/* Flux Range Section */}
                        <div className="mb-4">
                            <div className="flex justify-between items-center mb-3">
                                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-widest m-0">Flux Range</h4>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setLocalFilterType('today');
                                        setLocalStartDate(getCalculatedDateString('today', 'start'));
                                        setLocalEndDate(getCalculatedDateString('today', 'end'));
                                        setLocalStartTime(null);
                                        setLocalEndTime(null);
                                    }}
                                    className="text-[11px] font-bold text-rose-500 hover:text-rose-700 bg-transparent border-0 outline-none p-0 cursor-pointer uppercase tracking-wider transition-colors"
                                >
                                    Reset
                                </button>
                            </div>

                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Start Date</label>
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                        <input
                                            type="date"
                                            value={localStartDate}
                                            onChange={(e) => handleCustomDateChange(true, e.target.value)}
                                            className="border-0 bg-transparent text-xs text-slate-700 font-semibold outline-none focus:ring-0 p-0 w-full"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">End Date</label>
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                        <input
                                            type="date"
                                            value={localEndDate}
                                            onChange={(e) => handleCustomDateChange(false, e.target.value)}
                                            className="border-0 bg-transparent text-xs text-slate-700 font-semibold outline-none focus:ring-0 p-0 w-full"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Start Time</label>
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                        <input
                                            type="time"
                                            value={localStartTime || ''}
                                            onChange={(e) => setLocalStartTime(e.target.value || null)}
                                            className="border-0 bg-transparent text-xs text-slate-700 font-semibold outline-none focus:ring-0 p-0 w-full"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">End Time</label>
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                        <input
                                            type="time"
                                            value={localEndTime || ''}
                                            onChange={(e) => setLocalEndTime(e.target.value || null)}
                                            className="border-0 bg-transparent text-xs text-slate-700 font-semibold outline-none focus:ring-0 p-0 w-full"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <button
                            onClick={() => {
                                setFilterType(localFilterType);
                                if (localFilterType === 'custom') {
                                    setCustomStartDate(localStartDate);
                                    setCustomEndDate(localEndDate);
                                } else if (localFilterType === 'all') {
                                    setCustomStartDate('');
                                    setCustomEndDate('');
                                } else {
                                    setCustomStartDate(localStartDate);
                                    setCustomEndDate(localEndDate);
                                }
                                setStartTime(localStartTime || '');
                                setEndTime(localEndTime || '');
                                setPage(1);
                                setShowTimelineModal(false);
                            }}
                            className="w-full py-3 bg-[#2563EB] hover:bg-[#004ef8] text-white font-bold text-xs rounded-xl tracking-widest transition-all duration-200 uppercase mt-2 shadow-lg hover:shadow-xl active:scale-[0.98]"
                        >
                            Sync Range
                        </button>
                    </div>
                </Modal>
            )}

        </div>
    );
};

export default UniversalLeadsDashboard;
